import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users2, 
  Plus, 
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Mail,
  Phone,
  Building2,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_leave: 'bg-amber-50 text-amber-700 border-amber-200',
  terminated: 'bg-red-50 text-red-700 border-red-200'
};

const typeColors = {
  full_time: 'bg-blue-50 text-blue-700 border-blue-200',
  part_time: 'bg-purple-50 text-purple-700 border-purple-200',
  contract: 'bg-orange-50 text-orange-700 border-orange-200',
  intern: 'bg-slate-50 text-slate-700 border-slate-200'
};

const Employees = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({});
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  const statusLabels = {
    active: t('pages.employees.active'),
    on_leave: t('pages.employees.onLeave'),
    terminated: t('pages.employees.terminated')
  };

  const typeLabels = {
    full_time: t('pages.employees.fullTime'),
    part_time: t('pages.employees.partTime'),
    contract: t('pages.employees.contract'),
    intern: t('pages.employees.intern')
  };

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/departments`);
      setDepartments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingEmployee) {
        await axios.put(`${API_URL}/api/hr/employees/${editingEmployee.id}`, formData);
        toast.success(t('pages.employees.employeeUpdated'));
      } else {
        await axios.post(`${API_URL}/api/hr/employees`, formData);
        toast.success(t('pages.employees.employeeAdded'));
      }
      setShowDialog(false);
      setEditingEmployee(null);
      setFormData({});
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('pages.employees.failedToSave'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('pages.employees.confirmDelete'))) {
      try {
        await axios.delete(`${API_URL}/api/hr/employees/${id}`);
        toast.success(t('pages.employees.employeeDeleted'));
        fetchEmployees();
      } catch (err) {
        toast.error(t('pages.employees.failedToDelete'));
      }
    }
  };

  const openEdit = (emp) => {
    setEditingEmployee(emp);
    setFormData({ ...emp, hire_date: emp.hire_date?.split('T')[0] || '' });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingEmployee(null);
    setFormData({ employment_type: 'full_time', status: 'active' });
    setShowDialog(true);
  };

  const getDepartmentName = (id) => departments.find(d => d.id === id)?.name || '-';

  const filteredEmployees = employees
    .filter(e => filterDepartment === 'all' || e.department_id === filterDepartment)
    .filter(e => filterStatus === 'all' || e.status === filterStatus)
    .filter(e => !search || `${e.first_name} ${e.last_name}`.toLowerCase().includes(search.toLowerCase()) || e.email?.toLowerCase().includes(search.toLowerCase()));

  const stats = {
    total: employees.length,
    active: employees.filter(e => e.status === 'active').length,
    onLeave: employees.filter(e => e.status === 'on_leave').length,
    fullTime: employees.filter(e => e.employment_type === 'full_time').length
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.employees.title')}</h1>
          <p className="text-muted-foreground">{t('pages.employees.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Plus className="h-4 w-4" /> {t('pages.employees.addEmployee')}</Button>
      </div>

      <div className={`grid grid-cols-4 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">{t('pages.employees.totalEmployees')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{stats.active}</div><div className="text-sm text-muted-foreground">{t('pages.employees.active')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-amber-600">{stats.onLeave}</div><div className="text-sm text-muted-foreground">{t('pages.employees.onLeave')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{stats.fullTime}</div><div className="text-sm text-muted-foreground">{t('pages.employees.fullTime')}</div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
          <Input className={isRTL ? 'pr-10' : 'pl-10'} placeholder={t('pages.employees.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filterDepartment} onValueChange={setFilterDepartment}>
          <SelectTrigger className="w-48"><SelectValue placeholder={t('pages.employees.department')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.employees.allDepartments')}</SelectItem>
            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36"><SelectValue placeholder={t('pages.employees.status')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.employees.allStatus')}</SelectItem>
            <SelectItem value="active">{t('pages.employees.active')}</SelectItem>
            <SelectItem value="on_leave">{t('pages.employees.onLeave')}</SelectItem>
            <SelectItem value="terminated">{t('pages.employees.terminated')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.employees.employee')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.employees.id')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.employees.department')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.employees.position')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.employees.type')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.employees.status')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEmployees.map((emp) => (
              <TableRow key={emp.id}>
                <TableCell>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                      {emp.first_name?.[0]}{emp.last_name?.[0]}
                    </div>
                    <div>
                      <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                      <div className={`text-xs text-muted-foreground flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}><Mail className="h-3 w-3" /> {emp.email}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-muted-foreground">{emp.employee_id}</TableCell>
                <TableCell>{getDepartmentName(emp.department_id)}</TableCell>
                <TableCell>{emp.position || '-'}</TableCell>
                <TableCell><Badge className={typeColors[emp.employment_type]}>{typeLabels[emp.employment_type] || emp.employment_type}</Badge></TableCell>
                <TableCell><Badge className={statusColors[emp.status]}>{statusLabels[emp.status] || emp.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                      <DropdownMenuItem onClick={() => openEdit(emp)}><Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(emp.id)}>
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredEmployees.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <Users2 className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{t('pages.employees.noEmployees')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingEmployee ? t('pages.employees.editEmployee') : t('pages.employees.addEmployee')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.employees.employeeId')} *</Label>
                <Input value={formData.employee_id || ''} onChange={e => setFormData({...formData, employee_id: e.target.value})} placeholder="EMP001" />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.employees.department')}</Label>
                <Select value={formData.department_id || ''} onValueChange={v => setFormData({...formData, department_id: v})}>
                  <SelectTrigger><SelectValue placeholder={t('pages.employees.selectDepartment')} /></SelectTrigger>
                  <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.employees.firstName')} *</Label>
                <Input value={formData.first_name || ''} onChange={e => setFormData({...formData, first_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.employees.lastName')} *</Label>
                <Input value={formData.last_name || ''} onChange={e => setFormData({...formData, last_name: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('pages.employees.email')} *</Label>
              <Input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.employees.phone')}</Label>
                <Input value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.employees.position')}</Label>
                <Input value={formData.position || ''} onChange={e => setFormData({...formData, position: e.target.value})} placeholder="Software Engineer" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.employees.employmentType')}</Label>
                <Select value={formData.employment_type || 'full_time'} onValueChange={v => setFormData({...formData, employment_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">{t('pages.employees.fullTime')}</SelectItem>
                    <SelectItem value="part_time">{t('pages.employees.partTime')}</SelectItem>
                    <SelectItem value="contract">{t('pages.employees.contract')}</SelectItem>
                    <SelectItem value="intern">{t('pages.employees.intern')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('pages.employees.hireDate')}</Label>
                <Input type="date" value={formData.hire_date || ''} onChange={e => setFormData({...formData, hire_date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.employees.salary')}</Label>
                <Input type="number" value={formData.salary || ''} onChange={e => setFormData({...formData, salary: parseFloat(e.target.value) || 0})} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.employees.status')}</Label>
                <Select value={formData.status || 'active'} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('pages.employees.active')}</SelectItem>
                    <SelectItem value="on_leave">{t('pages.employees.onLeave')}</SelectItem>
                    <SelectItem value="terminated">{t('pages.employees.terminated')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{editingEmployee ? t('pages.common.edit') : t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
